import { createContext, useContext } from 'react';

export interface TwoFactorInfo {
  enabled: boolean;
  method: 'email' | 'sms' | 'totp';
  /** An administrator requires it: no Disable in Settings. */
  required?: boolean;
  hasBackupCodes: boolean;
  backupCodesRemaining: number;
}

export interface AuthUser {
  username: string;
  name: string;
  /** `support-l*` are VELOX technical support (docs/plans/2026-09-25-ruby-hq-support-role.md). */
  role: 'admin' | 'client' | 'support-l1' | 'support-l2';
  /** 0 clinic user · 1 VELOX L1 · 2 VELOX L2 · 3 Ruby admin. Read through
   *  `useAccess()`; the server's route table is the authority. */
  level?: 0 | 1 | 2 | 3;
  /** A support account that has not enrolled an authenticator yet — the
   *  server confines it to its own account routes, the shell to Settings. */
  totpRequired?: boolean;
  email?: string;
  phone?: string;
  hasPassword?: boolean;
  twoFactor?: TwoFactorInfo;
  /** Effective permission ids the user has — computed server-side via the
   *  `can()` helper against `PERMISSION_REGISTRY`. Used by client-side
   *  visibility gates (e.g. sidebar entries). Admins always have every
   *  registered permission. v1 ships with `view_workflow_map` only. */
  permissions?: string[];
  /** The user's assigned subrole (display fields only — never the permission
   *  map). Present only for client users with an active subrole; admins and
   *  unassigned clients have none. Lets permission-gated UIs name the role an
   *  admin must edit to grant a missing permission. */
  subrole?: { id: string; name: string };
  /** Per-user location scope (migration 0161): `{ clientId: [locationId | '__needs__'] }`.
   *  Absent, or no key for the selected clinic, = unrestricted there. Used by
   *  the browser for display only — every gate is server-side.
   *  Plan: docs/plans/2026-09-20-user-location-scope.md */
  locationScope?: Record<string, string[]>;
  /** The only pages (nav-item ids) this user may open, plus Account settings —
   *  set by a confined subrole such as Front Desk (migration 0169). Absent =
   *  every page. Shapes the menu, the landing page and the router; the server
   *  refuses the rest of the API (`page-gate.ts`). */
  pages?: string[];
}

export interface ClinicInfo {
  clientId: string;
  clinicName: string;
  enabledModules: string[];
  /** Per-client menu curation (migration 0143) — nav-item ids this clinic's
   *  sidebar omits, and the id `/` redirects to (`null` = first visible item).
   *  Resolved by `shell/nav-items.ts`. Optional on the wire so a browser tab
   *  open across the deploy (holding a pre-0143 `/api/auth/me` payload) reads
   *  as "nothing hidden" rather than throwing. */
  navHidden?: string[];
  navLanding?: string | null;
}

export interface AuthState {
  user: AuthUser | null;
  clinics: ClinicInfo[];
  selectedClinic: string | null;
  setSelectedClinic: (id: string) => void;
  login: (username: string, password: string) => Promise<Record<string, unknown>>;
  logout: () => Promise<void>;
  verifyOtp: (code: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
